import React from "react";

import { css } from "@emotion/css";
import { CellContext } from "@tanstack/react-table";
import { generateText } from "@tiptap/core";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useCopyToClipboard } from "@/hooks/useClipboard.ts";
import { formatTextToRichText } from "@/hooks/useRichTextState.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { CarePlan } from "@/models/CarePlanModel.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { ReadOnlyFileManager } from "@/uiKit/molecules/FileManager.tsx";
import { richTextExtensions } from "@/uiKit/molecules/RichText/RichTextSettings.ts";
import { RichTextViewer } from "@/uiKit/molecules/RichText/RichTextViewer.tsx";

export type DescriptionColumnProps = CellContext<CarePlan, string | undefined>;

export const DescriptionColumn: React.FC<DescriptionColumnProps> = ({ getValue, row: { original } }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const attachment = original.descriptionAttachment;
  const copyToClipboard = useCopyToClipboard();
  const richText = formatTextToRichText(getValue());
  const flatText = richText ? generateText(JSON.parse(richText ?? ""), richTextExtensions()) : undefined;

  return (
    <div className={styles.container}>
      <ReadOnlyFileManager
        id={`care-plan-history-objectives-file-${original.id}`}
        label={t("carePlans.historyObjectivesFile")}
        file={attachment}
      />
      {richText && (
        <Tooltip
          id={`care-plan-description-${original.id}`}
          placement="bottom-end"
          closeOnInteract={false}
          content={
            <>
              <CardHeader
                title={t("carePlans.historyObjectivesTitle")}
                action={
                  flatText && (
                    <Button
                      data-action="care-plan.description.copy"
                      className={styles.button}
                      variant="text"
                      onClick={() => copyToClipboard(flatText)}
                      startIcon={<Icons.copy className={styles.icon} />}
                    >
                      {t("carePlans.copyDescription")}
                    </Button>
                  )
                }
              />
              <CardContent>
                <RichTextViewer className={styles.richText} content={richText} />
              </CardContent>
              <CardFooter />
            </>
          }
        >
          <Button data-action="care-plan.description.see" variant="outlined">
            {t("carePlans.historyObjectivesText")}
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    gap: theme.spacing(4),
    alignItems: "center",
    minWidth: 0,
    overflow: "hidden",
  }),
  button: css({
    flex: "1 1",
    textWrap: "nowrap",
  }),
  richText: css({
    maxHeight: "30vh",
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
